import * as React from "react";

import { cn } from "@/lib/utils.js";

/**
 * Table component - Main table element (scrolling handled by parent container)
 * @param {Object} props - Component props
 * @param {string} [props.className] - Additional CSS classes
 * @param {React.Ref<HTMLTableElement>} ref - Forwarded ref
 * @param {...Object} props - Other table HTML attributes
 * @returns {JSX.Element} Table element
 */
const Table = React.forwardRef(
  /** @param {{ className?: string, children?: React.ReactNode, [key: string]: any }} props */
  ({ className, ...props }, ref) => (
    <table
      ref={ref}
      className={cn("w-full caption-bottom text-sm", className)}
      {...props}
    />
  )
);
Table.displayName = "Table";

/**
 * Table header component
 */
const TableHeader = React.forwardRef(
  /** @param {{ className?: string, children?: React.ReactNode, [key: string]: any }} props */
  ({ className, ...props }, ref) => (
    <thead ref={ref} className={cn("[&_tr]:border-b", className)} {...props} />
  )
);
TableHeader.displayName = "TableHeader";

/**
 * Table body component
 */
const TableBody = React.forwardRef(
  /** @param {{ className?: string, children?: React.ReactNode, [key: string]: any }} props */
  ({ className, ...props }, ref) => (
    <tbody
      ref={ref}
      className={cn("[&_tr:last-child]:border-0", className)}
      {...props}
    />
  )
);
TableBody.displayName = "TableBody";

/**
 * Table footer component
 */
const TableFooter = React.forwardRef(
  /** @param {{ className?: string, children?: React.ReactNode, [key: string]: any }} props */
  ({ className, ...props }, ref) => (
    <tfoot
      ref={ref}
      className={cn(
        "border-t bg-muted/50 font-medium [&>tr]:last:border-b-0",
        className
      )}
      {...props}
    />
  )
);
TableFooter.displayName = "TableFooter";

/**
 * Table row component
 */
const TableRow = React.forwardRef(
  /** @param {{ className?: string, children?: React.ReactNode, [key: string]: any }} props */
  ({ className, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn(
        "border-b border-border transition-colors hover:bg-muted dark:hover:bg-white/10 data-[state=selected]:bg-muted",
        className
      )}
      {...props}
    />
  )
);
TableRow.displayName = "TableRow";

/**
 * Table header cell component
 */
const TableHead = React.forwardRef(
  /** @param {{ className?: string, children?: React.ReactNode, [key: string]: any }} props */
  ({ className, ...props }, ref) => (
    <th
      ref={ref}
      className={cn(
        "h-12 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0",
        className
      )}
      {...props}
    />
  )
);
TableHead.displayName = "TableHead";

/**
 * Table data cell component
 */
const TableCell = React.forwardRef(
  /** @param {{ className?: string, children?: React.ReactNode, [key: string]: any }} props */
  ({ className, ...props }, ref) => (
    <td
      ref={ref}
      className={cn(
        "p-4 align-middle [&:has([role=checkbox])]:pr-0",
        className
      )}
      {...props}
    />
  )
);
TableCell.displayName = "TableCell";

/**
 * Table caption component
 */
const TableCaption = React.forwardRef(
  /** @param {{ className?: string, children?: React.ReactNode, [key: string]: any }} props */
  ({ className, ...props }, ref) => (
    <caption
      ref={ref}
      className={cn("mt-4 text-sm text-muted-foreground", className)}
      {...props}
    />
  )
);
TableCaption.displayName = "TableCaption";

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
};
